/**
 * Unit Booking Client Page
 *
 * Client component that loads unit data from API and displays booking interface.
 * Used for units stored in Firestore (not static lodges).
 */

'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import { getUnit, Unit } from '@/lib/admin/units-service';
import { lodges } from '@/lib/data/lodges';
import { useSoldOut } from '@/hooks/useSoldOut';
import UnitBookingClient from './UnitBookingClient';
import ImageGallery from '@/components/ui/ImageGallery';
import styles from './page.module.css';

interface Props {
  unitId: string;
}

/**
 * Amenity translations from Guesty English keys
 */
const AMENITY_I18N: Record<string, Record<string, string>> = {
  es: {
    'Air conditioning': 'Aire acondicionado',
    'BBQ grill': 'Parrilla BBQ',
    'Barbeque utensils': 'Utensilios de parrilla',
    'Body soap': 'Jabón corporal',
    'Breakfast': 'Desayuno',
    'Cleaning before checkout': 'Limpieza antes del checkout',
    'Cleaning products': 'Productos de limpieza',
    'Coffee': 'Café',
    'Coffee maker': 'Cafetera',
    'Conditioner': 'Acondicionador',
    'Cookware': 'Utensilios de cocina',
    'Dining table': 'Mesa de comedor',
    'Dishes and silverware': 'Vajilla y cubiertos',
    'Dishwasher': 'Lavavajillas',
    'Essentials': 'Artículos esenciales',
    'Garage': 'Garage',
    'Hair dryer': 'Secador de pelo',
    'Heating': 'Calefacción',
    'Hot water': 'Agua caliente',
    'Indoor fireplace': 'Chimenea interior',
    'Kettle': 'Hervidor',
    'Kitchen': 'Cocina',
    'Laptop friendly workspace': 'Espacio de trabajo',
    'Microwave': 'Microondas',
    'Oven': 'Horno',
    'Patio or balcony': 'Patio o balcón',
    'Refrigerator': 'Refrigerador',
    'Shampoo': 'Shampoo',
    'Ski in/Ski out': 'Ski in/Ski out',
    'Stove': 'Estufa',
    'TV': 'TV',
    'Toaster': 'Tostadora',
    'Wine glasses': 'Copas de vino',
    'Wireless Internet': 'Internet WiFi',
  },
  pt: {
    'Air conditioning': 'Ar condicionado',
    'BBQ grill': 'Churrasqueira',
    'Barbeque utensils': 'Utensílios de churrasco',
    'Body soap': 'Sabonete',
    'Breakfast': 'Café da manhã',
    'Cleaning before checkout': 'Limpeza antes do checkout',
    'Cleaning products': 'Produtos de limpeza',
    'Coffee': 'Café',
    'Coffee maker': 'Cafeteira',
    'Conditioner': 'Condicionador',
    'Cookware': 'Utensílios de cozinha',
    'Dining table': 'Mesa de jantar',
    'Dishes and silverware': 'Louças e talheres',
    'Dishwasher': 'Lava-louças',
    'Essentials': 'Itens essenciais',
    'Garage': 'Garagem',
    'Hair dryer': 'Secador de cabelo',
    'Heating': 'Aquecimento',
    'Hot water': 'Água quente',
    'Indoor fireplace': 'Lareira interna',
    'Kettle': 'Chaleira',
    'Kitchen': 'Cozinha',
    'Laptop friendly workspace': 'Espaço de trabalho',
    'Microwave': 'Micro-ondas',
    'Oven': 'Forno',
    'Patio or balcony': 'Pátio ou varanda',
    'Refrigerator': 'Geladeira',
    'Shampoo': 'Shampoo',
    'Ski in/Ski out': 'Ski in/Ski out',
    'Stove': 'Fogão',
    'TV': 'TV',
    'Toaster': 'Torradeira',
    'Wine glasses': 'Taças de vinho',
    'Wireless Internet': 'Internet WiFi',
  },
};

/**
 * Translate amenity name based on locale, falling back to formatted English
 */
function translateAmenity(amenity: string, locale: string): string {
  if (locale !== 'en' && AMENITY_I18N[locale]?.[amenity]) {
    return AMENITY_I18N[locale][amenity];
  }
  // Fallback: format the English key (split dashes, capitalize)
  return amenity
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function UnitBookingClientPage({ unitId: initialUnitId }: Props) {
  const t = useTranslations('bookingLodge');
  const rawLocale = useLocale();
  const locale = (['en', 'es', 'pt'].includes(rawLocale) ? rawLocale : 'en') as 'en' | 'es' | 'pt';
  const [unit, setUnit] = useState<Unit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isSoldOut, loaded: soldOutLoaded } = useSoldOut();
  // Tracks the effective minimum nights from the calendar (per-date or listing-level).
  // Updated via callback once unit loads and again when the user picks a check-in.
  const [effectiveMinNights, setEffectiveMinNights] = useState<number>(1);

  // Resolve real unit ID from URL (handles Firebase Hosting rewrite from placeholder)
  const [resolvedId, setResolvedId] = useState<string | null>(null);
  // Prefill from query params (?checkIn=&checkOut=&guests=), validated once on mount.
  const [prefill, setPrefill] = useState<{ checkIn: string; checkOut: string; guests?: number } | null>(null);

  useEffect(() => {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    // URL: /booking/lodges/{slug} → last segment is the slug
    const urlSlug = pathParts[pathParts.length - 1];

    if (urlSlug && urlSlug !== '_placeholder') {
      setResolvedId(urlSlug);
    } else if (initialUnitId && initialUnitId !== '_placeholder') {
      setResolvedId(initialUnitId);
    }

    // Parse + validate optional date/guest prefill from the query string.
    // Any validation failure discards the WHOLE prefill (no partial application).
    const params = new URLSearchParams(window.location.search);
    const checkInParam = params.get('checkIn');
    const checkOutParam = params.get('checkOut');
    const isoDateRe = /^\d{4}-\d{2}-\d{2}$/;

    if (checkInParam && checkOutParam && isoDateRe.test(checkInParam) && isoDateRe.test(checkOutParam)) {
      // Anti-timezone pattern: parse at local noon (matches UnitBookingClient).
      const checkInDate = new Date(`${checkInParam}T12:00:00`);
      const checkOutDate = new Date(`${checkOutParam}T12:00:00`);
      const now = new Date();
      const todayLocalMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      if (
        !Number.isNaN(checkInDate.getTime()) &&
        !Number.isNaN(checkOutDate.getTime()) &&
        checkOutDate > checkInDate &&
        checkInDate >= todayLocalMidnight
      ) {
        const guestsParam = params.get('guests');
        let guests: number | undefined;
        if (guestsParam !== null) {
          const parsedGuests = Number(guestsParam);
          if (Number.isInteger(parsedGuests)) {
            guests = parsedGuests;
          }
        }
        setPrefill({ checkIn: checkInParam, checkOut: checkOutParam, ...(guests !== undefined && { guests }) });
      }
    }
  }, [initialUnitId]);

  const fetchUnit = useCallback(async () => {
    if (!resolvedId) return;
    try {
      setLoading(true);
      setError(null);
      const fetchedUnit = await getUnit(resolvedId);
      setUnit(fetchedUnit);
      // Hero stat arranca con el general del listing (evita el "1" de un frame);
      // el calendario lo refina al mínimo per-fecha al elegir check-in.
      if (fetchedUnit?.pricing?.minimumNights) {
        setEffectiveMinNights(fetchedUnit.pricing.minimumNights);
      }
    } catch (err) {
      console.error('Error fetching unit:', err);
      setError(err instanceof Error ? err.message : 'Failed to load accommodation');
    } finally {
      setLoading(false);
    }
  }, [resolvedId]);

  useEffect(() => {
    fetchUnit();
  }, [fetchUnit]);

  // La unidad está agotada si ella misma lo está, o si pertenece a un lodge
  // agotado (settings/soldOut es la fuente de verdad dinámica; el campo
  // estático lodge.soldOut es el respaldo mientras esa clave no exista ahí).
  const unitLodge = unit ? lodges.find((l) => l.id === unit.locationId) : undefined;
  const soldOutBlocked =
    !!unit &&
    (isSoldOut(unit.id, false) || (!!unitLodge && isSoldOut(unitLodge.id, !!unitLodge.soldOut)));

  // Loading state - Premium Skeleton
  // Se espera también a que cargue el mapa de agotados (Firestore) antes de
  // decidir: evita mostrar el flujo de reserva y luego hacerlo desaparecer.
  if (loading || !soldOutLoaded) {
    return (
      <div className={styles.bookingPage}>
        {/* Hero Skeleton */}
        <section className={styles.hero} data-hero-background>
          <div className={styles.heroBackground}>
            <div className={`${styles.skeleton} ${styles.skeletonHeroImage}`} />
            <div className={styles.heroOverlay} />
          </div>
          <div className={styles.heroContent}>
            <div className={styles.skeletonBreadcrumb}>
              <div className={`${styles.skeleton} ${styles.skeletonBreadcrumbItem}`} />
              <span>/</span>
              <div className={`${styles.skeleton} ${styles.skeletonBreadcrumbItem}`} />
              <span>/</span>
              <div className={`${styles.skeleton} ${styles.skeletonBreadcrumbItem}`} />
            </div>
            <div className={`${styles.skeleton} ${styles.skeletonHeroTitle}`} />
            <div className={`${styles.skeleton} ${styles.skeletonHeroSubtitle}`} />
            <div className={`${styles.skeleton} ${styles.skeletonLocation}`} />
          </div>
        </section>

        {/* Stats Skeleton */}
        <section className={styles.statsSection}>
          <div className={styles.container}>
            <div className={styles.statsGrid}>
              {[1, 2, 3].map((i) => (
                <div key={i} className={styles.statCard}>
                  <div className={`${styles.skeleton} ${styles.skeletonStatNumber}`} />
                  <div className={`${styles.skeleton} ${styles.skeletonStatLabel}`} />
                  <div className={`${styles.skeleton} ${styles.skeletonStatDesc}`} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Booking Section Skeleton */}
        <section className={styles.bookingSection}>
          <div className={styles.container}>
            <div className={`${styles.skeleton} ${styles.skeletonBookingWidget}`} />
          </div>
        </section>

        {/* Details Skeleton */}
        <section className={styles.detailsSection}>
          <div className={styles.container}>
            <div className={styles.detailsGrid}>
              <div className={styles.detailsContent}>
                <div className={`${styles.skeleton} ${styles.skeletonDetailsTitle}`} />
                <div className={`${styles.skeleton} ${styles.skeletonDetailsPara}`} />
                <div className={`${styles.skeleton} ${styles.skeletonDetailsPara}`} style={{width: '80%'}} />
                <div className={styles.detailsBlock}>
                  <div className={`${styles.skeleton} ${styles.skeletonBlockTitle}`} />
                  <div className={styles.detailsList}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className={styles.detailsItem}>
                        <div className={`${styles.skeleton} ${styles.skeletonAmenityIcon}`} />
                        <div className={`${styles.skeleton} ${styles.skeletonAmenityText}`} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.galleryContainer}>
                <div className={`${styles.skeleton} ${styles.skeletonGallery}`} />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Error state (también cubre la unidad/lodge agotado: mismo trato que
  // "alojamiento no encontrado", sin pantalla ni textos nuevos)
  if (error || !unit || soldOutBlocked) {
    return (
      <div className={styles.bookingPage}>
        <section className={styles.hero} data-hero-background>
          <div className={styles.heroBackground}>
            <div className={styles.heroPlaceholder} />
            <div className={styles.heroOverlay} />
          </div>
          <div className={styles.heroContent}>
            <div className={styles.errorState}>
              <h1>{t('error.notFound')}</h1>
              <p>{error || t('error.notFoundDescription')}</p>
              <Link href="/booking/lodges" className={styles.backLink}>
                &larr; {t('error.backToAll')}
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Get hero image
  const heroImage = unit.images?.[0] || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80';
  const locationName = unit.location?.location || unit.location?.name || 'Chile';

  // Build stats from unit data. effectiveMinNights starts at the listing baseline
  // and updates reactively when the user picks a check-in with a different per-date minimum.
  const stats = [
    { number: `${unit.maxOccupancy}`, label: t('stats.guests'), description: t('stats.guestsDescription') },
    { number: `${unit.bedrooms || unit.bathroomCount}`, label: unit.bedrooms ? t('stats.bedrooms') : t('stats.bathrooms'), description: t('stats.roomsDescription') },
    { number: `${effectiveMinNights}`, label: t('stats.minNights'), description: t('stats.minNightsDescription') },
  ];

  return (
    <div className={styles.bookingPage}>
      {/* Hero Section */}
      <section className={styles.hero} data-hero-background>
        <div className={styles.heroBackground}>
          <Image
            src={heroImage}
            alt={`${unit.name} - ${unit.description?.slice(0, 50) || t('accommodation')}`}
            fill
            priority
            sizes="100vw"
            className={styles.heroImage}
          />
          <div className={styles.heroOverlay} />
        </div>
        <div className={styles.heroContent}>
          <div className={styles.breadcrumb}>
            <Link href="/booking" className={styles.breadcrumbLink}>
              {t('breadcrumb.booking')}
            </Link>
            <span className={styles.breadcrumbSeparator}>/</span>
            <Link href="/booking/lodges" className={styles.breadcrumbLink}>
              {t('breadcrumb.lodges')}
            </Link>
            <span className={styles.breadcrumbSeparator}>/</span>
            <span className={styles.breadcrumbCurrent}>{unit.name}</span>
          </div>
          <h1 className={styles.heroTitle}>{unit.name}</h1>
          <p className={styles.heroSubtitle}>
            {t('heroSubtitle', { bedConfig: unit.bedConfiguration, maxGuests: unit.maxOccupancy })}
          </p>
          <div className={styles.heroLocation}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span>{locationName}</span>
          </div>
        </div>

        {/* Mobile Stats Strip — visible only on mobile */}
        <div className={styles.heroMobileStats}>
          {stats.map((stat, index) => (
            <div key={stat.label} className={styles.heroMobileStatItem}>
              {index > 0 && <div className={styles.heroMobileStatDivider} />}
              <div className={styles.heroMobileStatInner}>
                <span className={styles.heroMobileStatNumber}>{stat.number}</span>
                <span className={styles.heroMobileStatLabel}>{stat.label}</span>
                <span className={styles.heroMobileStatSublabel}>{stat.description}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className={styles.statsSection}>
        <div className={styles.container}>
          <div className={styles.statsGrid}>
            {stats.map((stat) => (
              <div key={stat.label} className={styles.statCard}>
                <div className={styles.statNumber}>{stat.number}</div>
                <div className={styles.statLabel}>{stat.label}</div>
                <div className={styles.statDescription}>{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section (Client Component) — ActivityCrossSell renderiza internamente con acceso a fechas */}
      <UnitBookingClient unit={unit} onEffectiveMinNightsChange={setEffectiveMinNights} initialBooking={prefill ?? undefined} />

      {/* Details Section */}
      <section className={styles.detailsSection}>
        <div className={styles.container}>
          <div className={styles.detailsGrid}>
            <div className={styles.detailsContent}>
              <h2 className={styles.detailsTitle}>{t('aboutTitle')}</h2>
              <p className={styles.detailsDescription}>
                {unit.description || unit.spaceDescription || t('fallbackDescription', { unitName: unit.name })}
              </p>

              {/* Space Description */}
              {unit.spaceDescription && unit.spaceDescription !== unit.description && (
                <div className={styles.detailsBlock}>
                  <h3 className={styles.blockTitle}>{t('theSpace')}</h3>
                  <p className={styles.blockDescription}>{unit.spaceDescription}</p>
                </div>
              )}

              {/* Amenities */}
              {unit.amenities && unit.amenities.length > 0 && (
                <div className={styles.detailsBlock}>
                  <h3 className={styles.blockTitle}>{t('facilities')}</h3>
                  <ul className={styles.detailsList}>
                    {unit.amenities.slice(0, 10).map((amenity, index) => (
                      <li key={index} className={styles.detailsItem}>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="m5 12 7-7 7 7" />
                          <path d="M12 5v14" />
                        </svg>
                        <span>{translateAmenity(amenity, locale)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Access Instructions */}
              {unit.accessInstructions && (
                <div className={styles.detailsBlock}>
                  <h3 className={styles.blockTitle}>{t('gettingThere')}</h3>
                  <p className={styles.blockDescription}>{unit.accessInstructions}</p>
                </div>
              )}

              {/* Neighborhood */}
              {unit.neighborhoodDescription && (
                <div className={styles.detailsBlock}>
                  <h3 className={styles.blockTitle}>{t('theNeighborhood')}</h3>
                  <p className={styles.blockDescription}>{unit.neighborhoodDescription}</p>
                </div>
              )}
            </div>

            {/* Gallery */}
            {unit.images && unit.images.length > 0 && (
              <div className={styles.galleryContainer}>
                <ImageGallery
                  images={unit.images}
                  alts={unit.imageAlts}
                  title={t('galleryTitle', { unitName: unit.name })}
                  aspectRatio={16 / 10}
                  locale={locale}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>{t('cta.title')}</h2>
            <p className={styles.ctaDescription}>
              {t('cta.description', { lodgeName: unit.name })}
            </p>
            <div className={styles.ctaButtons}>
              <Link href="/contact" className={styles.ctaButtonPrimary}>
                {t('cta.contactConcierge')}
              </Link>
              <Link href="/booking/lodges" className={styles.ctaButtonSecondary}>
                {t('cta.viewAllLodges')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
